import Box from "@amzn/meridian/box";
import Tag from "@amzn/meridian/tag";
import Text from "@amzn/meridian/text";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { AppealsComponent } from "../../../components/appeals/AppealsComponent";
import LoaderBox from "../../../components/loader/LoaderBox";
import { tableMapping } from "../../../components/table/definitions";
import {
    IRealPropertyResponse,
    useGetRealPropertyDetailsQuery,
} from "../../../endpoints/getRealProperty";
import { useBreadcrumbs } from "../../../features/breadcrumbs/hooks";
import {
    formatDate,
    formatInputWithPrefixAndSuffix,
    formatLongString,
    formatPhoneToolLink,
    formatString,
    formatStringWithUnit,
} from "../../../utils/formattingUtils";
import { Error404 } from "../../error-404/Error404";
import ArtifactDetailsPageSkeleton from "../templates/ArtifactDetailsPageSkeleton";
import ArtifactInformation from "../templates/ArtifactInformation";
import { IRowAttributes } from "../templates/AttributesPanel";
import MailsTo from "../templates/MailsTo";
import { AssociatedParcels } from "./AssociatedParcels";
import { PropertySpaceTableComponent } from "../../../components/table/PropertySpaceTableComponent";
import { addThousandSeparator } from "./utils";

export const PropertyDetails: React.FC = () => {
    const { propertyId } = useParams();
    const { updateBreadcrumbs } = useBreadcrumbs();
    const ARTIFACT_TYPE = "REAL_PROPERTY";

    const { data, isLoading, isSuccess, refetch, isUninitialized } = useGetRealPropertyDetailsQuery(
        {
            artifactId: propertyId,
            businessUseCase: "SMART",
        }
    );

    useEffect(() => {
        if (isSuccess) {
            updateBreadcrumbs([
                { text: "Home", href: "/" },
                {
                    text: data.propertyDetails.propertyNumber,
                    href: `/property-details/${propertyId}`,
                },
            ]);
        }
    }, [data, isSuccess]);

    useEffect(() => {
        !isUninitialized ? refetch() : null;
    }, []);

    return (
        <>
            {isLoading ? (
                <LoaderBox loadingText="Loading property..." />
            ) : isSuccess ? (
                <ArtifactDetailsPageSkeleton
                    detailsSectionHeight="260px"
                    artifactDetails={getPropertyDetailsPanel(data)}
                    tabs={getTabs(propertyId, ARTIFACT_TYPE, data)}
                    data={data}
                    refetch={refetch}
                />
            ) : (
                <Error404 />
            )}
        </>
    );
};

const getPropertyDetailsPanel = (data: IRealPropertyResponse) => {
    return [
        {
            columns: [
                {
                    label: "Site code",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.siteCode)}
                        </Text>
                    ),
                },
                {
                    label: "Property type",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.propertyType)}
                        </Text>
                    ),
                },
                {
                    label: "Property sub type",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.propertySubType)}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Property number",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.propertyNumber)}
                        </Text>
                    ),
                },
                {
                    label: "Gross building area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.propertyDetails.grossBuildingArea,
                                data.propertyDetails.grossBuildingAreaUnit
                            )}
                        </Text>
                    ),
                },
                {
                    label: "Gross leasable area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.propertyDetails.grossLeasableArea,
                                data.propertyDetails.grossLeasableAreaUnit
                            )}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Site area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.propertyDetails.siteArea,
                                data.propertyDetails.siteAreaUnit
                            )}
                        </Text>
                    ),
                },
                {
                    label: "Site usable area",
                    value: (
                        <Text color="primary" type="h100">
                            {formatStringWithUnit(
                                data.propertyDetails.siteUsableArea,
                                data.propertyDetails.siteUsableAreaUnit
                            )}
                        </Text>
                    ),
                },
                {
                    label: "Year built",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.yearBuilt)}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "General ledger number",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.generalLedgerNumber)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const getTabs = (propertyId: string, artifactType: string, data: IRealPropertyResponse) => {
    return [
        {
            label: "Property information",
            value: "information",
            content: (
                <ArtifactInformation
                    statusAndOwnershipInformationPanel={getStatusAndOwnershipInformationPanel(data)}
                    financialInformationPanel={getFinancialInformationPanel(data)}
                    otherInformationPanel={getOtherInformationPanel(data)}
                    spacesPanel={getSpacePanel(data)}
                    artifactId={propertyId}
                    artifactType={artifactType}
                />
            ),
        },
        {
            label: "Assessments & Appeals",
            value: "artifact-assessment",
            content: (
                <AppealsComponent
                    propertyId={propertyId}
                    isValidated={data.isValidated}
                    missingFields={data.missingFields}
                />
            ),
        },
        {
            label: "Mails to the property",
            value: "artifacts-mails",
            content: <MailsTo artifactId={propertyId} associatedArtifactType={artifactType} />,
        },
        {
            label: "Associated parcels",
            value: "artifact-parcels",
            content: (
                <AssociatedParcels
                    propertyNumber={data["propertyDetails"].propertyNumber}
                    artifactType={artifactType}
                    artifactId={propertyId}
                />
            ),
        },
    ];
};

const getStatusAndOwnershipInformationPanel = (data: IRealPropertyResponse) => {
    return [
        {
            columns: [
                {
                    label: "Property status",
                    value: (
                        <>
                            {"ACTIVE" === data.propertyDetails.status ? (
                                <Tag type="success">Active</Tag>
                            ) : (
                                <Tag type="error">In Active</Tag>
                            )}
                        </>
                    ),
                },

                {
                    label: "Ownership status",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.ownershipStatus)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Entity name",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.entityName)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Entity code",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.entityCode)}
                        </Text>
                    ),
                },

                {
                    label: "Sec. division/groups",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.secDivision)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Date opened",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.propertyDetails.dateOpened)}
                        </Text>
                    ),
                },

                {
                    label: "Date closed/Lease end",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.propertyDetails.dateClosed)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const getFinancialInformationPanel = (data: IRealPropertyResponse) => {
    return [
        {
            columns: [
                {
                    label: "Sale date",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.propertyDetails.saleDate)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Lease rate",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.leaseRate)}
                        </Text>
                    ),
                },
                {
                    label: "Sale price",
                    value: (
                        <Text color="primary" type="h100">
                            ${addThousandSeparator(formatString(data.propertyDetails.salePrice))}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Construction costs",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.constructionCosts)}
                        </Text>
                    ),
                },
            ],
        },

        {
            columns: [
                {
                    label: "Currency",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.currency)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const getOtherInformationPanel = (data: IRealPropertyResponse) => {
    return [
        {
            columns: [
                {
                    label: "Contacts",
                    value: formatLongString(data.propertyDetails.contacts),
                },
                {
                    label: "Assigned users",
                    value: formatPhoneToolLink(data.propertyDetails.assignedTo),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Property revision date",
                    value: (
                        <Text color="primary" type="h100">
                            {formatDate(data.propertyDetails.dateRevised)}
                        </Text>
                    ),
                },
            ],
        },
        {
            columns: [
                {
                    label: "Property other description",
                    value: (
                        <Text color="primary" type="h100">
                            {formatString(data.propertyDetails.otherDescription)}
                        </Text>
                    ),
                },
            ],
        },
    ];
};

const SpacePanel: React.FC<{ data: IRealPropertyResponse }> = ({ data }) => {
    const transformedSpaceData = React.useMemo(
        () =>
            data.spaceDetails.map(space => ({
                spaceName: space.spaceName,
                area: formatInputWithPrefixAndSuffix(space.avgFloorArea),
                status: (
                    <>
                        {"ACTIVE" === space.status ? (
                            <Tag type="success">Active</Tag>
                        ) : (
                            <Tag type="error">In Active</Tag>
                        )}
                    </>
                ),
            })),
        [data.spaceDetails]
    );

    return (
        <Box backgroundColor={"principal"}>
            <PropertySpaceTableComponent
                config={tableMapping["property-page-spaces"]}
                rows={transformedSpaceData}
            />
        </Box>
    );
};

const getSpacePanel = (data: IRealPropertyResponse): IRowAttributes[] => {
    return [
        {
            columns: [
                {
                    label: "",
                    value: <SpacePanel data={data} />,
                },
            ],
        },
    ];
};
